import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";
import {
  getAccessToken,
  clearAllTokens,
  saveAccessToken,
  saveRefreshToken,
} from "../utils/storage.utils";
import { queryClient } from "./query-client";
import { refreshToken } from "../services/auth-refresh.service";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

interface QueueItem {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueueItem[] = [];
let isSessionInvalidated = false;

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token != null) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });

  failedQueue = [];
};

// Endpoints that authenticate themselves and must not have the stored
// access token attached or refreshed by the interceptor (the pending-2FA
// token is passed explicitly and the stored token would override it).
const SELF_AUTH_ENDPOINTS = [
  "auth/sign-in",
  "auth/refresh",
  "auth/2mfa/verify",
  "auth/2mfa/verify-backup-code",
];

const isSelfAuthEndpoint = (url: string): boolean => {
  // Strip query string so "auth/sign-in?foo=bar" still matches.
  const path = url.split("?")[0];
  return SELF_AUTH_ENDPOINTS.some(
    // Match exactly OR when the baseURL path prefix prepends a slash.
    // e.g. "auth/sign-in" and "/auth/sign-in" both match; "evil/auth/sign-in" does not.
    (endpoint) => path === endpoint || path === `/${endpoint}`
  );
};

// A 403 on GET /documents/{id} is a legitimate authorization failure handled
// by the RequestAccessDialog UI, not an auth-expiry case. Every other 403
// should still fall through to the standard auth/logout flow.
const DOCUMENT_GET_PATH_RE = /^\/?documents\/[^/?]+(\?.*)?$/;

const isDocumentGetRequest = (method: string | undefined, url: string | undefined): boolean => {
  if (method == null || url == null) return false;
  if (method.toLowerCase() !== "get") return false;
  return DOCUMENT_GET_PATH_RE.test(url);
};

// The backend signals "out of credits" via a 402 with detail containing this
// string. Other 402 reasons (expired card, plan downgrade) must not trigger
// the top-up dialog because topping up won't fix them.
const OUT_OF_BALANCE_DETAIL = "Not enough balance";

const isOutOfBalanceError = (data: unknown): boolean => {
  if (typeof data !== "object" || data == null) return false;
  const detail = (data as { detail?: unknown }).detail;
  return typeof detail === "string" && detail.includes(OUT_OF_BALANCE_DETAIL);
};

// Request interceptor to add authorization header
axiosInstance.interceptors.request.use(
  async (config) => {
    const url = config.url ?? "";
    if (isSelfAuthEndpoint(url)) {
      return config;
    }

    const token = await getAccessToken();

    if (token != null && token !== "") {
      config.headers.set("Authorization", `Bearer ${token}`);
      isSessionInvalidated = false;
    }

    return config;
  },
  (error: unknown) => {
    return Promise.reject(error);
  }
);

async function invalidateSession(): Promise<void> {
  isSessionInvalidated = true;
  await clearAllTokens();

  toast.error("Session Expired", {
    description: "Your session has expired. Please log in again.",
  });

  queryClient.setQueryData(["user"], null);
  queryClient.removeQueries();
}

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: unknown) => {
    if (!axios.isAxiosError<unknown>(error) || error.config == null) {
      return Promise.reject(error);
    }

    const originalRequest: InternalAxiosRequestConfig = error.config;
    const status = error.response?.status ?? 0;
    const url = originalRequest.url ?? "";

    // 402 Payment Required — only trigger top-up dialog when the backend
    // signals "Not enough balance". Other 402 reasons (expired card, plan
    // downgrade) propagate to the caller untouched.
    if (status === 402 && isOutOfBalanceError(error.response?.data)) {
      try {
        const { topUpEvents, TOP_UP_REQUIRED } = await import("../utils/events");
        topUpEvents.emit(TOP_UP_REQUIRED);
      } catch {
        // Import failure must not shadow the 402 error propagation.
      }
    }

    // 403 = authorization failure. Token refresh will not help.
    //   - GET /documents/{id}: let it propagate silently so useDocument can
    //     trigger the RequestAccessDialog.
    //   - Any other 403: surface a global toast so the user isn't left in the
    //     dark. Still propagate so callers can handle if needed.
    if (status === 403) {
      if (!isDocumentGetRequest(originalRequest.method, url)) {
        toast.error("Permission denied", {
          description: "You don't have permission to perform this action.",
        });
      }
      return Promise.reject(error);
    }

    let shouldLogout = false;

    if (status === 401) {
      if (isSessionInvalidated) {
        return Promise.reject(error);
      }

      if (!isSelfAuthEndpoint(url)) {
        if (isRefreshing) {
          return new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.set("Authorization", `Bearer ${token}`);
              return axiosInstance(originalRequest);
            })
            .catch((err: unknown) => {
              return Promise.reject(err);
            });
        }

        isRefreshing = true;

        try {
          const { access_token, refresh_token } = await refreshToken();
          await saveAccessToken(access_token);
          await saveRefreshToken(refresh_token);
          isRefreshing = false;
          processQueue(null, access_token);
          originalRequest.headers.set("Authorization", `Bearer ${access_token}`);
          return axiosInstance(originalRequest);
        } catch (refreshError: unknown) {
          processQueue(refreshError);
          shouldLogout = true;
        } finally {
          isRefreshing = false;
        }
      } else if (
        isSelfAuthEndpoint(url) &&
        (url === "auth/refresh" || url.endsWith("/auth/refresh"))
      ) {
        // A 401 from the refresh endpoint means the refresh token itself is
        // expired or revoked — log the user out. Other self-auth endpoints
        // (sign-in, 2FA verify) must NOT trigger logout on 401.
        shouldLogout = true;
      }
    }

    if (shouldLogout) {
      await invalidateSession();
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
